import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';
import { socialAppSelectors } from 'app/modules/social/state/app';
import { mediaSelectors } from 'app/modules/social/state/media';
import { AppState, Interval, Timeout, WindowWithWebkitAudio } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanTracks, getUserMedia } from 'app/utils/media/stream';
import User from 'entity/User';

import { ActiveSpeakerService } from '../../active-speaker/active-speaker.service';
import { DeviceService } from '../../device/device.service';
import _ from 'lodash';

const arrayAverage = (array: number[]) => array.reduce((a, b) => a + b) / array.length;

// Run connection checker every N seconds
const MONITOR_INTERVAL_SECONDS = 0.1;
const AVERAGE_STACK_LENGTH = 20;
const DEFAULT_AVERAGE_STACK = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

export default class ActiveSpeakerListener extends Loggable {
  private user?: User;

  // Monitor Intervals
  private monitorInterval?: Interval;
  // Sound
  private audioContext?: AudioContext;
  private soundAnalyser?: AnalyserNode;
  private dataArray?: Uint8Array;
  private averageStack = [...DEFAULT_AVERAGE_STACK];
  private isOpenModeActive = false;
  private isUserPresenting = false;
  private isUserActiveSpeaker = false;
  private isUserSpeaking = false;

  private defaultCheckStream?: MediaStream;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isMicEnabled = true;
  private isCrossRoom = false;

  private clearListTimeout?: Timeout;
  public clearUserSpeakingTimeout?: Timeout;
  private activeSpeakerThrottle = 4000;

  public constructor(
    private deviceService: DeviceService,
    private store: Store<AppState>,
    private activeSpeakerService: ActiveSpeakerService,
  ) {
    super();
    this.setDisplayName('ActiveSpeakerListener');
    this.deviceService.audioStream$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((stream) => this.createAudioStream(stream));

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isMicEnabled = enabled));

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => (this.isCrossRoom = isCrossRoom));

    this.store
      .select(socialAppSelectors.isUserActiveSpeaker)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserActiveSpeaker) => (this.isUserActiveSpeaker = isUserActiveSpeaker));

    this.store
      .select(socialAppSelectors.isOpenModeActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isOpenModeActive) => (this.isOpenModeActive = isOpenModeActive));

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => {
        this.isUserPresenting = isUserPresenting;
      });

    this.store
      .select(socialAppSelectors.userIsSpeaking)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserSpeaking) => {
        this.isUserSpeaking = isUserSpeaking;
      });

    // Initiate audioContext, soundAnalyser and dataArray
    window.AudioContext = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;

    // Initiate soundChecker to run constantly
    this.monitorInterval = setInterval(() => this.soundChecker(), MONITOR_INTERVAL_SECONDS * 1000);
  }

  public async destroy(): Promise<void> {
    this.info('Destroying active speaker listener');

    await cleanTracks(this.defaultCheckStream, this);
    this.defaultCheckStream = undefined;

    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    this.monitorInterval = undefined;

    const { audioContext } = this;
    this.audioContext = undefined;
    try {
      if (audioContext?.state !== 'closed') {
        await audioContext?.close();
      }
    } catch (err) {
      this.error('Error Closing Audio Context', err);
    }

    this.averageStack = [...DEFAULT_AVERAGE_STACK];
    this.soundAnalyser = undefined;
    this.dataArray = undefined;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async createAudioStream(newStream: MediaStream | undefined): Promise<void> {
    const { audioContext } = this;
    this.audioContext = undefined;
    try {
      await audioContext?.close();
    } catch (err) {
      this.error('Error Closing Audio Context', err);
    }

    await cleanTracks(this.defaultCheckStream, this);
    this.defaultCheckStream = undefined;

    if (!newStream) {
      return;
    }

    const constraints: MediaStreamConstraints = { audio: { deviceId: { exact: this.deviceService.mic?.deviceId } } };

    try {
      this.defaultCheckStream = await getUserMedia(constraints, this);
    } catch (e) {
      this.error('Error getting user media', e);
    }

    if (!this.defaultCheckStream) {
      this.error('Error getting user media, defaultCheckStream is undefined');
      return;
    }

    // Create and connect the new analyser instance to the new stream
    this.audioContext = new AudioContext();
    this.soundAnalyser = this.audioContext.createAnalyser();
    this.soundAnalyser.fftSize = 32;
    this.dataArray = new Uint8Array(this.soundAnalyser.frequencyBinCount);
    this.audioContext.createMediaStreamSource(this.defaultCheckStream).connect(this.soundAnalyser);
  }

  private soundChecker(): void {
    // don't run if no audio context and mic is disabled
    if (!this.audioContext) {
      return;
    }

    // don't run if mic is enable but reset the notification flag
    if (!this.isMicEnabled) {
      this.averageStack = [...DEFAULT_AVERAGE_STACK];
      return;
    }

    if (this.dataArray === undefined) {
      return;
    }

    // Calculate average of this run
    this.soundAnalyser?.getByteTimeDomainData(this.dataArray);
    const diffSum = this.dataArray.reduce((acc: number, value: number): number => acc + Math.abs(128 - value), 0);
    const diffAvg = diffSum / this.dataArray.length;

    // Calculate average of last N runs
    this.averageStack.unshift(diffAvg);
    this.averageStack.length = AVERAGE_STACK_LENGTH;
    const stackDiffAvg = arrayAverage(this.averageStack);

    // Throw Notification
    if (stackDiffAvg > 2) {
      if (this.isOpenModeActive) {
        // For presentation active speakers
        this.getActiveSpeaker();
      }
      // General active speakers: for CC
      this.updateUserActiveSpeaker();
    }
  }

  private updateUserActiveSpeaker(): void {
    if (this.user && !this.isUserSpeaking) {
      this.updateIsUserSpeaking(true);
      this.setTimerUserSpeaking();
    } else {
      clearTimeout(this.clearUserSpeakingTimeout);
      this.setTimerUserSpeaking();
    }
  }

  private getActiveSpeaker(): void {
    // If the user is on gallery and starts to speak, it will be added to speaking participants
    if (this.user && !this.isUserActiveSpeaker) {
      this.updateStorageActiveSpeaker(true, !!this.isUserPresenting);
      this.setTimer();
    } else {
      clearTimeout(this.clearListTimeout);
      this.setTimer();
    }
  }

  private removeUserFromList(userId?: string): void {
    // Remove speaking participant after 5 Seconds
    if (userId && this.isUserActiveSpeaker) {
      this.updateStorageActiveSpeaker(false, !!this.isUserPresenting);
    }
  }

  private setTimerUserSpeaking(): void {
    this.clearUserSpeakingTimeout = setTimeout(() => {
      this.updateIsUserSpeaking(false);
    }, 3000);
  }

  private setTimer(): void {
    this.clearListTimeout = setTimeout(() => {
      this.removeUserFromList(this.user?.userId as string);
    }, 5000);
  }

  private updateStorageActiveSpeaker = _.throttle((isSpeaking: boolean, isPresenter?: boolean): void => {
    this.activeSpeakerService.sendActiveSpeakerMessage(
      isSpeaking,
      this.isCrossRoom,
      this.user?.userId as string,
      isPresenter,
    );
  }, this.activeSpeakerThrottle);

  public updateIsUserSpeaking(isSpeaking: boolean): void {
    this.activeSpeakerService.updateUserActiveSpeakerState(isSpeaking);
  }
}
